import React, { FC, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { api } from '../functions/api';
import { ApiPath } from '../api/ApiPath';
import { useApiItems } from '../hooks/useApiItems';
import { Customer } from '../customers/dto/Customer';
import { Site } from '../sites/dto/Site';
import { AliasEntityType } from './dto/EntityAlias';

interface Props {
  onClose: () => void;
  onCreated: () => void;
  /** Prefilled when adding an alias from a conflict the bot keeps asking about. */
  initialSurface?: string;
}

/**
 * Teaches the catalogue a word.
 *
 * The entity is chosen from a list rather than typed, because an alias pointing at an id
 * nobody has is worse than no alias: it resolves to nothing and reports no error.
 */
export const EntityAliasCreate: FC<Props> = ({ onClose, onCreated, initialSurface }) => {
  const { t } = useTranslation();
  const [entityType, setEntityType] = useState<AliasEntityType>('site');
  const [entityId, setEntityId] = useState('');
  const [surface, setSurface] = useState(initialSurface ?? '');
  const [saving, setSaving] = useState(false);

  const [customers, customersLoading] = useApiItems<Customer>(ApiPath.Customers);
  const [sites, sitesLoading] = useApiItems<Site>(ApiPath.Sites);

  const loading = entityType === 'site' ? sitesLoading : customersLoading;
  const options: Array<{ id: string; name: string }> =
    entityType === 'site'
      ? (sites ?? []).map((site) => ({ id: String(site.id), name: site.name }))
      : (customers ?? []).map((customer) => ({ id: String(customer.id), name: customer.name }));

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!entityId || !surface.trim()) return;

    setSaving(true);
    try {
      await api({
        url: ApiPath.EntityAliases,
        method: 'post',
        data: { entityType, entityId, surface: surface.trim() },
      });
      toast.success(t('Name added'));
      onCreated();
      onClose();
    } catch {
      toast.error(t('Could not add that name'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal.Header closeButton>
        <Modal.Title>{t('Add a name')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={submit}>
          <Form.Group className="mb-3">
            <Form.Label>{t('What kind of thing does it name?')}</Form.Label>
            <Form.Select
              value={entityType}
              onChange={(event) => {
                setEntityType(event.target.value as AliasEntityType);
                setEntityId('');
              }}
            >
              <option value="site">{t('Site')}</option>
              <option value="customer">{t('Customer')}</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('Which one')}</Form.Label>
            {loading ? (
              <Form.Select disabled>
                <option>{t('Loading...')}</option>
              </Form.Select>
            ) : (
              <Form.Select value={entityId} onChange={(event) => setEntityId(event.target.value)}>
                <option value="">{t('Choose…')}</option>
                {options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </Form.Select>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('The word people say')}</Form.Label>
            <Form.Control
              value={surface}
              onChange={(event) => setSurface(event.target.value)}
              placeholder={t('For example, a short name used on the radio').toString()}
            />
          </Form.Group>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={onClose} type="button">
              {t('Cancel')}
            </Button>
            <Button type="submit" disabled={saving || !entityId || !surface.trim()}>
              {saving ? <Spinner size="sm" animation="border" /> : t('Add')}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </>
  );
};
